import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { REPORTS, IMPORT } from "@/constants/testIds";
import { Download, FileBarChart2, FileText, Calendar, User, FileSpreadsheet, BarChart3, Plus, X, Upload, Users, BookOpenCheck } from "lucide-react";
import PrintPreviewButton from "@/components/PrintPreviewButton";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const now = new Date();
const fmt = (n) =>
  Number(n || 0).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const attendance = (t = {}) =>
  Number(t.attendance_adults || 0) +
  Number(t.attendance_youth || 0) +
  Number(t.attendance_kids || 0);

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const YEARS = Array.from({ length: 7 }, (_, i) => now.getFullYear() - 3 + i);

const TABS = [
  { key: "monthly", label: "Reporte Mensual", icon: Calendar, testid: REPORTS.tabMonthly },
  { key: "member", label: "Por Miembro", icon: User, testid: REPORTS.tabMember },
  { key: "annual", label: "Balance Anual", icon: FileBarChart2, testid: REPORTS.tabAnnual },
  { key: "multiyear", label: "Multi-Año", icon: BarChart3, testid: REPORTS.tabMultiYear },
  { key: "cells", label: "Por Célula", icon: Users, testid: REPORTS.tabCells },
  { key: "import", label: "Importar Excel", icon: Upload, testid: REPORTS.tabImport },
];

export default function Reports() {
  const [tab, setTab] = useState("monthly");
  return (
    <div data-testid={REPORTS.page}>
      <div className="no-print">
        <PageHeader
          title="Reportes"
          subtitle="Informes mensuales, contribuciones por miembro y balance anual"
        />
        <div className="px-8 pt-6">
          <div className="flex items-center justify-between border-b border-[#d9cfb8]">
            <div className="flex">
              {TABS.map(({ key, label, icon: Icon, testid }) => (
                <button
                  key={key}
                  data-testid={testid}
                  onClick={() => setTab(key)}
                  className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-widest border-b-2 -mb-px transition ${
                    tab === key
                      ? "border-[#c8a04a] text-[#1c1a14] bg-white"
                      : "border-transparent text-[#8b7e5e] hover:text-[#1c1a14]"
                  }`}
                >
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="p-8 print:p-0">
        {tab === "monthly" && <MonthlyTab />}
        {tab === "member" && <MemberTab />}
        {tab === "annual" && <AnnualTab />}
        {tab === "multiyear" && <MultiYearTab />}
        {tab === "cells" && <CellsTab />}
        {tab === "import" && <ImportTab />}
      </div>
    </div>
  );
}

/* -------------------- Monthly -------------------- */

function MonthlyTab() {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const generate = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/reports/monthly", { params: { year, month } });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error generando reporte");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get("/reports/monthly.pdf", {
        params: { year, month },
        responseType: "blob",
      });
      saveBlob(res.data, `reporte_mensual_${year}_${String(month).padStart(2, "0")}.pdf`);
    } catch (e) {
      setError("No se pudo descargar el PDF");
    }
  };

  const downloadExcel = async () => {
    try {
      const res = await api.get("/export/monthly.xlsx", {
        params: { year, month },
        responseType: "blob",
      });
      saveBlob(
        res.data,
        `reporte_mensual_${year}_${String(month).padStart(2, "0")}.xlsx`,
        XLSX_MIME,
      );
    } catch (e) {
      setError("No se pudo descargar el Excel");
    }
  };

  const totals = data?.totals || {};
  const byCat = data?.expenses_by_category || {};
  const catEntries = Object.entries(byCat);

  return (
    <Panel>
      <Controls>
        <Field label="Año">
          <Select
            testid={REPORTS.yearSelect}
            value={year}
            onChange={(v) => setYear(Number(v))}
            options={YEARS.map((y) => ({ value: y, label: y }))}
          />
        </Field>
        <Field label="Mes">
          <Select
            testid={REPORTS.monthSelect}
            value={month}
            onChange={(v) => setMonth(Number(v))}
            options={MONTHS.map((m, i) => ({ value: i + 1, label: m }))}
          />
        </Field>
        <PrimaryBtn testid={REPORTS.generateButton} onClick={generate} disabled={loading}>
          <FileText size={14} /> {loading ? "Generando..." : "Generar"}
        </PrimaryBtn>
        <SecondaryBtn
          testid={REPORTS.downloadPdfButton}
          onClick={downloadPdf}
          disabled={!data || loading}
        >
          <Download size={14} /> PDF
        </SecondaryBtn>
        <SecondaryBtn
          testid={REPORTS.downloadExcelMonthlyButton}
          onClick={downloadExcel}
          disabled={loading}
        >
          <FileSpreadsheet size={14} /> Excel
        </SecondaryBtn>
        <PrintPreviewButton
          testid={REPORTS.printButton}
          disabled={!data || loading}
        />
      </Controls>

      <ErrorMsg error={error} />

      {data && (
        <div data-testid={REPORTS.preview} className="mt-6 print-area">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#8b7e5e]">
            {MONTHS[month - 1]} {year}
          </div>
          <h3 className="font-serif text-2xl text-[#1c1a14] mb-4">Resumen del mes</h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Stat label="Diezmos" value={totals.tithes} />
            <Stat label="Ofrendas" value={totals.offering} />
            <Stat label="Ofrendas especiales" value={totals.special_offering} />
            <Stat label="Entradas" value={data.ingresos} highlight />
            <Stat label="Gastos" value={data.gastos} />
            <Stat label="Neto del mes" value={data.neto} highlight />
            <Stat label="Saldo anterior" value={data.saldo_anterior} />
            <Stat label="Saldo final" value={data.saldo_final} highlight />
            <Stat label="Asistencia adultos" value={totals.attendance_adults} isCount />
            <Stat label="Asistencia jóvenes" value={totals.attendance_youth} isCount />
            <Stat label="Asistencia niños" value={totals.attendance_kids} isCount />
            <Stat label="Servicios" value={(data.captures || []).length} isCount />
          </div>

          {Array.isArray(data.captures) && data.captures.length > 0 && (
            <div className="overflow-auto border border-[#d9cfb8] bg-white">
              <table className="ajar-spreadsheet w-full">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Servicio</th>
                    <th className="text-right">Diezmos</th>
                    <th className="text-right">Ofrendas</th>
                    <th className="text-right">Especiales</th>
                    <th className="text-right">Total</th>
                    <th className="text-right">Asist.</th>
                  </tr>
                </thead>
                <tbody>
                  {data.captures.map((s) => {
                    const total =
                      Number(s.tithes || 0) +
                      Number(s.offering || 0) +
                      Number(s.special_offering || 0);
                    return (
                      <tr key={s.id || `${s.date}-${s.service_type}`}>
                        <td>{s.date}</td>
                        <td>{s.service_type}</td>
                        <td className="text-right tabular-nums">$ {fmt(s.tithes)}</td>
                        <td className="text-right tabular-nums">$ {fmt(s.offering)}</td>
                        <td className="text-right tabular-nums">$ {fmt(s.special_offering)}</td>
                        <td className="text-right tabular-nums font-medium">$ {fmt(total)}</td>
                        <td className="text-right tabular-nums">
                          {Number(s.attendance_adults || 0) +
                            Number(s.attendance_youth || 0) +
                            Number(s.attendance_kids || 0)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {catEntries.length > 0 && (
            <div className="mt-6">
              <h4 className="font-serif text-lg text-[#1c1a14] mb-2">Gastos por categoría</h4>
              <div className="border border-[#d9cfb8] bg-white">
                <table className="ajar-spreadsheet w-full">
                  <thead>
                    <tr>
                      <th>Categoría</th>
                      <th className="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catEntries.map(([cat, total]) => (
                      <tr key={cat}>
                        <td>{cat}</td>
                        <td className="text-right tabular-nums">$ {fmt(total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

/* -------------------- Member -------------------- */

function MemberTab() {
  const [year, setYear] = useState(now.getFullYear());
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/members")
      .then(({ data }) => setMembers(data))
      .catch(() => setMembers([]));
  }, []);

  const generate = async () => {
    if (!memberId) {
      setError("Selecciona un miembro");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/reports/member/${memberId}`, { params: { year } });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error generando reporte");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/reports/member/${memberId}.pdf`, {
        params: { year },
        responseType: "blob",
      });
      const name = data?.member?.custom_id || "miembro";
      saveBlob(res.data, `reporte_${name}_${year}.pdf`);
    } catch (e) {
      setError("No se pudo descargar el PDF");
    }
  };

  const totals = data?.totals || {};

  return (
    <Panel>
      <Controls>
        <Field label="Miembro" wide>
          <select
            data-testid={REPORTS.memberSelect}
            value={memberId}
            onChange={(e) => setMemberId(e.target.value)}
            className={selectCls}
          >
            <option value="">— Seleccionar —</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.custom_id} · {m.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Año">
          <Select
            testid={REPORTS.yearSelect}
            value={year}
            onChange={(v) => setYear(Number(v))}
            options={YEARS.map((y) => ({ value: y, label: y }))}
          />
        </Field>
        <PrimaryBtn testid={REPORTS.generateButton} onClick={generate} disabled={loading}>
          <FileText size={14} /> {loading ? "Generando..." : "Generar"}
        </PrimaryBtn>
        <SecondaryBtn
          testid={REPORTS.downloadPdfButton}
          onClick={downloadPdf}
          disabled={!data || loading}
        >
          <Download size={14} /> PDF
        </SecondaryBtn>
        <PrintPreviewButton
          testid={REPORTS.printButton}
          disabled={!data || loading}
        />
      </Controls>

      <ErrorMsg error={error} />

      {data && data.member && (
        <div data-testid={REPORTS.preview} className="mt-6 print-area">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#8b7e5e]">
            {data.member.custom_id}
          </div>
          <h3 className="font-serif text-2xl text-[#1c1a14] mb-4">
            {data.member.name} — {year}
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Stat label="Diezmos" value={totals.diezmos} />
            <Stat label="Ofrendas" value={totals.ofrendas} />
            <Stat label="Total aportado" value={totals.total} highlight />
            <Stat label="Sobres" value={totals.count} isCount />
          </div>

          {Array.isArray(data.by_month) && data.by_month.length > 0 && (
            <div className="border border-[#d9cfb8] bg-white">
              <table className="ajar-spreadsheet w-full">
                <thead>
                  <tr>
                    <th>Mes</th>
                    <th className="text-right">Diezmos</th>
                    <th className="text-right">Ofrendas</th>
                    <th className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_month.map((m) => (
                    <tr key={m.month}>
                      <td>{m.label}</td>
                      <td className="text-right tabular-nums">$ {fmt(m.diezmos)}</td>
                      <td className="text-right tabular-nums">$ {fmt(m.ofrendas)}</td>
                      <td className="text-right tabular-nums font-medium">$ {fmt(m.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <MemberDetail envelopes={data.envelopes || []} />
        </div>
      )}
    </Panel>
  );
}

function MemberDetail({ envelopes }) {
  const [open, setOpen] = useState(true);
  if (!envelopes.length) return null;

  // Group chronologically; envelopes already sorted by date in backend
  const fmtDate = (iso) => {
    if (!iso || iso.length < 10) return iso || "—";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-serif text-lg text-[#1c1a14]">
          Detalle por fecha
          <span className="ml-2 text-xs text-[#8b7e5e]">
            ({envelopes.length} aporte{envelopes.length === 1 ? "" : "s"})
          </span>
        </h4>
        <button
          data-testid={REPORTS.memberDetailToggle}
          onClick={() => setOpen((o) => !o)}
          className="text-[11px] uppercase tracking-widest text-[#1c2a3a] hover:text-[#0f1a2c]"
        >
          {open ? "Ocultar detalle" : "Ver detalle"}
        </button>
      </div>
      {open && (
        <div className="border border-[#d9cfb8] bg-white overflow-x-auto">
          <table
            data-testid={REPORTS.memberDetailTable}
            className="ajar-spreadsheet w-full"
          >
            <thead>
              <tr>
                <th style={{ width: 110 }}>Fecha</th>
                <th>Servicio</th>
                <th style={{ width: 100 }}>Tipo</th>
                <th className="text-right" style={{ width: 130 }}>
                  Monto
                </th>
                <th>Notas</th>
              </tr>
            </thead>
            <tbody>
              {envelopes.map((e) => (
                <tr key={e.id} data-testid={REPORTS.memberDetailRow(e.id)}>
                  <td className="tabular-nums">{fmtDate(e.date)}</td>
                  <td>{e.service_type || "—"}</td>
                  <td>
                    <span
                      className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 border ${
                        e.type === "Diezmo"
                          ? "border-[#1c2a3a] text-[#1c2a3a]"
                          : "border-[#c8a04a] text-[#7a5b0f]"
                      }`}
                    >
                      {e.type}
                    </span>
                  </td>
                  <td className="text-right tabular-nums font-medium">
                    $ {fmt(e.amount)}
                  </td>
                  <td className="text-xs text-[#5d5849]">{e.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* -------------------- Annual -------------------- */

function AnnualTab() {
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const generate = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/reports/annual", { params: { year } });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error generando reporte");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get("/reports/annual.pdf", {
        params: { year },
        responseType: "blob",
      });
      saveBlob(res.data, `balance_anual_${year}.pdf`);
    } catch (e) {
      setError("No se pudo descargar el PDF");
    }
  };

  const downloadExcel = async () => {
    try {
      const res = await api.get("/export/annual.xlsx", {
        params: { year },
        responseType: "blob",
      });
      saveBlob(res.data, `balance_anual_${year}.xlsx`, XLSX_MIME);
    } catch (e) {
      setError("No se pudo descargar el Excel");
    }
  };

  const [bundleLoading, setBundleLoading] = useState(false);
  const downloadAnnualBundle = async () => {
    setBundleLoading(true);
    setError("");
    try {
      const res = await api.get("/reports/annual-bundle.pdf", {
        params: { year },
        responseType: "blob",
        timeout: 60000,
      });
      saveBlob(res.data, `ajar_anio_completo_${year}.pdf`);
    } catch (e) {
      setError("No se pudo generar el PDF del año completo");
    } finally {
      setBundleLoading(false);
    }
  };

  return (
    <Panel>
      <Controls>
        <Field label="Año">
          <Select
            testid={REPORTS.annualYearSelect}
            value={year}
            onChange={(v) => setYear(Number(v))}
            options={YEARS.map((y) => ({ value: y, label: y }))}
          />
        </Field>
        <PrimaryBtn testid={REPORTS.generateButton} onClick={generate} disabled={loading}>
          <FileText size={14} /> {loading ? "Generando..." : "Generar"}
        </PrimaryBtn>
        <SecondaryBtn
          testid={REPORTS.downloadPdfButton}
          onClick={downloadPdf}
          disabled={!data || loading}
        >
          <Download size={14} /> PDF
        </SecondaryBtn>
        <SecondaryBtn
          testid={REPORTS.downloadExcelAnnualButton}
          onClick={downloadExcel}
          disabled={loading}
        >
          <FileSpreadsheet size={14} /> Excel
        </SecondaryBtn>
        <SecondaryBtn
          testid={REPORTS.downloadAnnualBundleButton}
          onClick={downloadAnnualBundle}
          disabled={bundleLoading || loading}
          title="Descarga un PDF combinado con el balance anual, los 12 reportes mensuales y el reporte por células del año seleccionado"
        >
          <BookOpenCheck size={14} /> {bundleLoading ? "Generando..." : "Año Completo"}
        </SecondaryBtn>
        <PrintPreviewButton
          testid={REPORTS.printButton}
          disabled={!data || loading}
        />
      </Controls>

      <ErrorMsg error={error} />

      {data && (
        <div data-testid={REPORTS.preview} className="mt-6 print-area">
          <h3 className="font-serif text-2xl text-[#1c1a14] mb-4">Balance Anual {year}</h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Stat label="Entradas anuales" value={data.annual_income} />
            <Stat label="Gastos anuales" value={data.annual_expenses} />
            <Stat label="Neto anual" value={data.annual_neto} highlight />
            <Stat label="Asistencia total" value={data.annual_attendance} isCount />
            <Stat label="Saldo inicial" value={data.saldo_inicial} />
            <Stat label="Saldo final" value={data.saldo_final} highlight />
          </div>

          {Array.isArray(data.months) && data.months.length > 0 && (
            <div className="overflow-auto border border-[#d9cfb8] bg-white">
              <table className="ajar-spreadsheet w-full">
                <thead>
                  <tr>
                    <th>Mes</th>
                    <th className="text-right">Diezmos</th>
                    <th className="text-right">Ofrendas</th>
                    <th className="text-right">Especiales</th>
                    <th className="text-right">Entradas</th>
                    <th className="text-right">Gastos</th>
                    <th className="text-right">Neto</th>
                    <th className="text-right">Saldo Final</th>
                  </tr>
                </thead>
                <tbody>
                  {data.months.map((m) => (
                    <tr key={m.month}>
                      <td>{m.label}</td>
                      <td className="text-right tabular-nums">$ {fmt(m.tithes)}</td>
                      <td className="text-right tabular-nums">$ {fmt(m.offering)}</td>
                      <td className="text-right tabular-nums">$ {fmt(m.special_offering)}</td>
                      <td className="text-right tabular-nums">$ {fmt(m.ingresos)}</td>
                      <td className="text-right tabular-nums">$ {fmt(m.gastos)}</td>
                      <td className="text-right tabular-nums font-medium">$ {fmt(m.neto)}</td>
                      <td className="text-right tabular-nums">$ {fmt(m.saldo_final)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

/* -------------------- Multi-Year -------------------- */

const SERIES_COLORS = ["#1c2a3a", "#c8a04a"];

function MultiYearTab() {
  const currentYear = now.getFullYear();
  const [selected, setSelected] = useState([currentYear - 1, currentYear]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const setYearAt = (idx, value) => {
    const next = [...selected];
    next[idx] = Number(value);
    setSelected(next);
  };

  const addYear = () => {
    if (selected.length >= 2) return;
    const candidate = selected.includes(currentYear) ? currentYear - 1 : currentYear;
    setSelected([...selected, candidate]);
  };

  const removeYear = (idx) => {
    if (selected.length <= 1) return;
    setSelected(selected.filter((_, i) => i !== idx));
  };

  const generate = async () => {
    const unique = Array.from(new Set(selected.filter((y) => Number.isInteger(y))));
    if (unique.length === 0) {
      setError("Selecciona al menos un año");
      return;
    }
    if (unique.length !== selected.length) {
      setError("Los años seleccionados deben ser distintos");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/reports/multi-year", {
        params: { years: unique.join(",") },
      });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error generando comparativo");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const years = (data?.years || selected).join(",");
      const res = await api.get("/reports/multi-year.pdf", {
        params: { years },
        responseType: "blob",
      });
      const label = years.replace(/,/g, "_vs_");
      saveBlob(res.data, `comparativo_multianio_${label}.pdf`);
    } catch (e) {
      setError("No se pudo descargar el PDF");
    }
  };

  const chartData = buildMultiYearChartData(data);
  const seriesYears = (data?.data || []).map((d) => d.year);

  return (
    <Panel>
      <Controls>
        {selected.map((y, idx) => (
          // Positional slot: index is semantically meaningful (year-N selector).
          // Composite key avoids React re-mount churn when the same year is
          // picked twice in different slots.
          <Field key={`${idx}-${y}`} label={`Año ${idx + 1}`}>
            <div className="flex items-center gap-2">
              <Select
                testid={REPORTS.multiYearYearSelect(idx)}
                value={y}
                onChange={(v) => setYearAt(idx, v)}
                options={YEARS.map((yr) => ({ value: yr, label: yr }))}
              />
              {selected.length > 1 && (
                <button
                  data-testid={REPORTS.multiYearRemove(idx)}
                  onClick={() => removeYear(idx)}
                  className="text-[#8b7e5e] hover:text-red-600 p-1"
                  title="Quitar año"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </Field>
        ))}
        {selected.length < 2 && (
          <SecondaryBtn testid={REPORTS.multiYearAdd} onClick={addYear}>
            <Plus size={14} /> Añadir año
          </SecondaryBtn>
        )}
        <PrimaryBtn
          testid={REPORTS.multiYearGenerate}
          onClick={generate}
          disabled={loading}
        >
          <FileText size={14} /> {loading ? "Generando..." : "Generar"}
        </PrimaryBtn>
        <SecondaryBtn
          testid={REPORTS.multiYearDownloadPdf}
          onClick={downloadPdf}
          disabled={!data || loading}
        >
          <Download size={14} /> PDF
        </SecondaryBtn>
        <PrintPreviewButton
          testid={REPORTS.multiYearPrintButton}
          disabled={!data || loading}
        />
      </Controls>

      <ErrorMsg error={error} />

      {data && (
        <div data-testid={REPORTS.preview} className="mt-6 space-y-8 print-area">
          <div>
            <h3 className="font-serif text-2xl text-[#1c1a14] mb-4">
              Comparativo Multi-Año {seriesYears.join(" vs ")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {(data.data || []).map((y) => (
                <div key={y.year} className="border border-[#d9cfb8] bg-[#fbf7ee] px-4 py-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">
                    {y.year}
                  </div>
                  <div className="text-xs text-[#5a4f36] mt-1">
                    Entradas: <span className="font-medium">$ {fmt(y.totals.ingresos)}</span>
                  </div>
                  <div className="text-xs text-[#5a4f36]">
                    Gastos: <span className="font-medium">$ {fmt(y.totals.gastos)}</span>
                  </div>
                  <div className="font-serif text-xl tabular-nums text-[#7a5b0f] mt-1">
                    Neto $ {fmt(y.totals.neto)}
                  </div>
                </div>
              ))}
              {data.deltas && (
                <>
                  <DeltaCard label="Δ Entradas" value={data.deltas.ingresos_pct} />
                  <DeltaCard label="Δ Gastos" value={data.deltas.gastos_pct} />
                  <DeltaCard label="Δ Neto" value={data.deltas.neto_pct} />
                  <DeltaCard label="Δ Asistencia" value={data.deltas.attendance_pct} />
                </>
              )}
            </div>
          </div>

          <div
            data-testid={REPORTS.multiYearChartIngresosGastos}
            className="border border-[#d9cfb8] bg-white p-4"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8b7e5e] mb-2">
              Comparativo
            </div>
            <h4 className="font-serif text-lg text-[#1c1a14] mb-3">Entradas vs Gastos por mes</h4>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer initialDimension={{ width: 600, height: 260 }}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5dec7" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#5a4f36" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#5a4f36" }} />
                  <Tooltip formatter={(v) => `$ ${fmt(v)}`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {seriesYears.map((yr, i) => (
                    <Bar
                      key={`in-${yr}`}
                      dataKey={`ingresos_${yr}`}
                      name={`Entradas ${yr}`}
                      fill={SERIES_COLORS[i]}
                    />
                  ))}
                  {seriesYears.map((yr, i) => (
                    <Bar
                      key={`gst-${yr}`}
                      dataKey={`gastos_${yr}`}
                      name={`Gastos ${yr}`}
                      fill={SERIES_COLORS[i]}
                      fillOpacity={0.45}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div
            data-testid={REPORTS.multiYearChartBalance}
            className="border border-[#d9cfb8] bg-white p-4"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8b7e5e] mb-2">
              Balance neto
            </div>
            <h4 className="font-serif text-lg text-[#1c1a14] mb-3">Neto mensual por año</h4>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer initialDimension={{ width: 600, height: 260 }}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5dec7" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#5a4f36" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#5a4f36" }} />
                  <Tooltip formatter={(v) => `$ ${fmt(v)}`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {seriesYears.map((yr, i) => (
                    <Line
                      key={`neto-${yr}`}
                      type="monotone"
                      dataKey={`neto_${yr}`}
                      name={`Neto ${yr}`}
                      stroke={SERIES_COLORS[i]}
                      strokeWidth={2.2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="overflow-auto border border-[#d9cfb8] bg-white">
            <table className="ajar-spreadsheet w-full">
              <thead>
                <tr>
                  <th>Mes</th>
                  {seriesYears.map((yr) => (
                    <th key={`h-in-${yr}`} className="text-right">Entradas {yr}</th>
                  ))}
                  {seriesYears.map((yr) => (
                    <th key={`h-gs-${yr}`} className="text-right">Gastos {yr}</th>
                  ))}
                  {seriesYears.map((yr) => (
                    <th key={`h-nt-${yr}`} className="text-right">Neto {yr}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chartData.map((row) => (
                  <tr key={row.month}>
                    <td>{row.month}</td>
                    {seriesYears.map((yr) => (
                      <td key={`b-in-${yr}-${row.month}`} className="text-right tabular-nums">
                        $ {fmt(row[`ingresos_${yr}`])}
                      </td>
                    ))}
                    {seriesYears.map((yr) => (
                      <td key={`b-gs-${yr}-${row.month}`} className="text-right tabular-nums">
                        $ {fmt(row[`gastos_${yr}`])}
                      </td>
                    ))}
                    {seriesYears.map((yr) => (
                      <td
                        key={`b-nt-${yr}-${row.month}`}
                        className="text-right tabular-nums font-medium"
                      >
                        $ {fmt(row[`neto_${yr}`])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Panel>
  );
}

function buildMultiYearChartData(report) {
  if (!report || !Array.isArray(report.data) || report.data.length === 0) return [];
  const monthLabels = MONTHS.map((m) => m.slice(0, 3));
  return monthLabels.map((label, i) => {
    const row = { month: label };
    for (const yearBlock of report.data) {
      const m = yearBlock.monthly?.[i] || {};
      row[`ingresos_${yearBlock.year}`] = Number(m.ingresos || 0);
      row[`gastos_${yearBlock.year}`] = Number(m.gastos || 0);
      row[`neto_${yearBlock.year}`] = Number(m.neto || 0);
    }
    return row;
  });
}

function DeltaCard({ label, value }) {
  const pct = value == null ? null : Number(value);
  const positive = pct != null && pct >= 0;
  return (
    <div className="border border-[#d9cfb8] bg-[#fbf7ee] px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">{label}</div>
      <div
        className={`font-serif text-xl tabular-nums ${
          pct == null ? "text-[#8b7e5e]" : positive ? "text-emerald-700" : "text-red-700"
        }`}
      >
        {pct == null ? "—" : `${positive ? "+" : ""}${pct.toFixed(1)}%`}
      </div>
    </div>
  );
}

/* -------------------- Per-Cell (Por Célula) -------------------- */

const MONTH_OPTIONS_WITH_ALL = [
  { value: 0, label: "Todo el año" },
  ...MONTHS.map((m, i) => ({ value: i + 1, label: m })),
];

function CellsTab() {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(0); // 0 = annual
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const generate = async () => {
    setLoading(true);
    setError("");
    try {
      const params = { year };
      if (month) params.month = month;
      const { data } = await api.get("/reports/cells", { params });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error generando reporte por célula");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const params = { year };
      if (month) params.month = month;
      const res = await api.get("/reports/cells.pdf", { params, responseType: "blob" });
      const suffix = month ? `${year}_${String(month).padStart(2, "0")}` : `${year}`;
      saveBlob(res.data, `reporte_por_celula_${suffix}.pdf`);
    } catch (e) {
      setError("No se pudo descargar el PDF");
    }
  };

  const downloadXlsx = async () => {
    try {
      const params = { year };
      if (month) params.month = month;
      const res = await api.get("/export/cells.xlsx", { params, responseType: "blob" });
      const suffix = month ? `${year}_${String(month).padStart(2, "0")}` : `${year}`;
      saveBlob(res.data, `reporte_celulas_${suffix}.xlsx`);
    } catch (e) {
      setError("No se pudo descargar el Excel");
    }
  };

  const totals = data?.totals || {};
  const byCell = data?.by_cell || [];
  const byCounty = data?.by_county || [];
  const matrix = data?.monthly_matrix || null;
  const scopeLabel = data?.label || "";

  return (
    <Panel>
      <Controls>
        <Field label="Año">
          <Select
            testid={REPORTS.cellsYearSelect}
            value={year}
            onChange={(v) => setYear(Number(v))}
            options={YEARS.map((y) => ({ value: y, label: y }))}
          />
        </Field>
        <Field label="Mes">
          <Select
            testid={REPORTS.cellsMonthSelect}
            value={month}
            onChange={(v) => setMonth(Number(v))}
            options={MONTH_OPTIONS_WITH_ALL}
          />
        </Field>
        <PrimaryBtn testid={REPORTS.cellsGenerateButton} onClick={generate} disabled={loading}>
          <FileText size={14} /> {loading ? "Generando..." : "Generar"}
        </PrimaryBtn>
        <SecondaryBtn
          testid={REPORTS.cellsDownloadPdfButton}
          onClick={downloadPdf}
          disabled={!data || loading}
        >
          <Download size={14} /> PDF
        </SecondaryBtn>
        <SecondaryBtn
          testid={REPORTS.cellsDownloadXlsxButton}
          onClick={downloadXlsx}
          disabled={!data || loading}
        >
          <FileSpreadsheet size={14} /> Excel
        </SecondaryBtn>
        <PrintPreviewButton
          testid={REPORTS.cellsPrintButton}
          disabled={!data || loading}
        />
      </Controls>

      <ErrorMsg error={error} />

      {data && (
        <div data-testid={REPORTS.preview} className="mt-6 print-area">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#8b7e5e]">{scopeLabel}</div>
          <h3 className="font-serif text-2xl text-[#1c1a14] mb-4">Reporte por Célula</h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Stat label="Células" value={totals.cells_count} isCount />
            <Stat label="Activas" value={totals.active_count} isCount />
            <Stat label="Sesiones" value={totals.sessions} isCount />
            <Stat label="Asistencia" value={totals.attendance} isCount />
            <Stat label="Nuevos" value={totals.attendance_new} isCount />
            <Stat label="Niños" value={totals.attendance_kids} isCount />
            <Stat label="Prom./sesión" value={totals.avg_attendance} isCount />
            <Stat label="Ofrenda" value={totals.offering} highlight />
          </div>

          <CellsTopChart byCell={byCell} />

          <div className="overflow-auto border border-[#d9cfb8] bg-white">
            <table data-testid={REPORTS.cellsTable} className="ajar-spreadsheet w-full">
              <thead>
                <tr>
                  <th>Célula</th>
                  <th>Líder</th>
                  <th>Condado</th>
                  <th className="text-right">Sesiones</th>
                  <th className="text-right">Asistencia</th>
                  <th className="text-right">Nuevos</th>
                  <th className="text-right">Niños</th>
                  <th className="text-right">Prom.</th>
                  <th className="text-right">Ofrenda</th>
                </tr>
              </thead>
              <tbody>
                {byCell.length === 0 && (
                  <tr><td colSpan={9} className="text-center text-[#8b7e5e] py-4">Sin datos para el periodo</td></tr>
                )}
                {byCell.map((c) => (
                  <tr
                    key={c.cell_id || `orphan-${c.name}`}
                    data-testid={c.cell_id ? REPORTS.cellsRow(c.cell_id) : undefined}
                  >
                    <td>
                      {c.name}
                      {!c.active && c.cell_id && (
                        <span className="ml-2 text-[10px] uppercase tracking-widest text-[#8b7e5e]">
                          inactiva
                        </span>
                      )}
                    </td>
                    <td>{c.leader}</td>
                    <td>{c.county}</td>
                    <td className="text-right tabular-nums">{c.sessions}</td>
                    <td className="text-right tabular-nums">{c.attendance}</td>
                    <td className="text-right tabular-nums">{c.attendance_new}</td>
                    <td className="text-right tabular-nums">{c.attendance_kids}</td>
                    <td className="text-right tabular-nums">{c.avg_attendance}</td>
                    <td className="text-right tabular-nums font-medium">$ {fmt(c.offering)}</td>
                  </tr>
                ))}
              </tbody>
              {byCell.length > 0 && (
                <tfoot>
                  <tr className="font-semibold bg-[#f5efdf]">
                    <td colSpan={3}>TOTAL</td>
                    <td className="text-right tabular-nums">{totals.sessions}</td>
                    <td className="text-right tabular-nums">{totals.attendance}</td>
                    <td className="text-right tabular-nums">{totals.attendance_new}</td>
                    <td className="text-right tabular-nums">{totals.attendance_kids}</td>
                    <td className="text-right tabular-nums">{totals.avg_attendance}</td>
                    <td className="text-right tabular-nums">$ {fmt(totals.offering)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {byCounty.length > 1 && (
            <div className="mt-6">
              <h4 className="font-serif text-lg text-[#1c1a14] mb-2">Resumen por Condado</h4>
              <div className="border border-[#d9cfb8] bg-white">
                <table className="ajar-spreadsheet w-full">
                  <thead>
                    <tr>
                      <th>Condado</th>
                      <th className="text-right">Células</th>
                      <th className="text-right">Sesiones</th>
                      <th className="text-right">Asistencia</th>
                      <th className="text-right">Ofrenda</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byCounty.map((b) => (
                      <tr key={b.county}>
                        <td>{b.county}</td>
                        <td className="text-right tabular-nums">{b.cells_count}</td>
                        <td className="text-right tabular-nums">{b.sessions}</td>
                        <td className="text-right tabular-nums">{b.attendance}</td>
                        <td className="text-right tabular-nums">$ {fmt(b.offering)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {matrix && matrix.length > 0 && (
            <div className="mt-6">
              <h4 className="font-serif text-lg text-[#1c1a14] mb-2">Ofrenda Mensual por Célula</h4>
              <div className="overflow-auto border border-[#d9cfb8] bg-white">
                <table data-testid={REPORTS.cellsMonthlyMatrix} className="ajar-spreadsheet w-full">
                  <thead>
                    <tr>
                      <th>Célula</th>
                      {MONTHS.map((m) => <th key={m} className="text-right">{m.slice(0, 3)}</th>)}
                      <th className="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matrix.map((entry) => {
                      const rowTotal = entry.months.reduce((s, m) => s + Number(m.offering || 0), 0);
                      return (
                        <tr key={entry.cell_id}>
                          <td>{entry.name}</td>
                          {entry.months.map((m) => (
                            <td key={m.month} className="text-right tabular-nums">$ {fmt(m.offering)}</td>
                          ))}
                          <td className="text-right tabular-nums font-medium">$ {fmt(rowTotal)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

/* Top 5 Células bar chart with Asistencia / Ofrenda toggle */
function CellsTopChart({ byCell }) {
  const [metric, setMetric] = useState("attendance"); // "attendance" | "offering"

  const rows = (byCell || []).filter((c) => c.cell_id);
  if (rows.length === 0) return null;

  const sorted = [...rows].sort((a, b) =>
    metric === "attendance"
      ? Number(b.attendance || 0) - Number(a.attendance || 0)
      : Number(b.offering || 0) - Number(a.offering || 0),
  );
  const top = sorted.slice(0, 5).map((c) => ({
    name: c.name,
    attendance: Number(c.attendance || 0),
    offering: Number(c.offering || 0),
  }));

  const allZero = top.every((t) => t[metric] === 0);

  return (
    <div
      data-testid={REPORTS.cellsTopChart}
      className="mt-2 mb-6 border border-[#d9cfb8] bg-white p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#8b7e5e]">
            Top 5 Células
          </div>
          <h4 className="font-serif text-lg text-[#1c1a14]">
            {metric === "attendance" ? "Por Asistencia" : "Por Ofrenda"}
          </h4>
        </div>
        <div className="inline-flex border border-[#c8a04a] overflow-hidden">
          <button
            data-testid={REPORTS.cellsTopToggleAttendance}
            onClick={() => setMetric("attendance")}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-widest transition ${
              metric === "attendance"
                ? "bg-[#1c2a3a] text-[#e7c879]"
                : "bg-white text-[#7a5b0f] hover:bg-[#fdf7e1]"
            }`}
          >
            Asistencia
          </button>
          <button
            data-testid={REPORTS.cellsTopToggleOffering}
            onClick={() => setMetric("offering")}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-widest border-l border-[#c8a04a] transition ${
              metric === "offering"
                ? "bg-[#1c2a3a] text-[#e7c879]"
                : "bg-white text-[#7a5b0f] hover:bg-[#fdf7e1]"
            }`}
          >
            Ofrenda
          </button>
        </div>
      </div>
      {allZero ? (
        <p className="text-xs text-[#8b7e5e] py-6 text-center">
          Sin datos de {metric === "attendance" ? "asistencia" : "ofrenda"} en este periodo
        </p>
      ) : (
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer initialDimension={{ width: 600, height: 260 }}>
            <BarChart data={top} layout="vertical" margin={{ left: 24, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5dec7" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 11, fill: "#5a4f36" }}
                tickFormatter={(v) => (metric === "offering" ? `$${fmt(v)}` : v)}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 11, fill: "#5a4f36" }}
                width={140}
              />
              <Tooltip
                formatter={(v) =>
                  metric === "offering" ? `$ ${fmt(v)}` : `${v} personas`
                }
              />
              <Bar
                dataKey={metric}
                name={metric === "attendance" ? "Asistencia" : "Ofrenda"}
                fill={metric === "attendance" ? "#1c2a3a" : "#c8a04a"}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

/* -------------------- Shared -------------------- */

function Panel({ children }) {
  return (
    <div
      className="bg-white border border-[#d9cfb8] p-6"
      style={{ borderTop: "3px solid #c8a04a" }}
    >
      {children}
    </div>
  );
}

function Controls({ children }) {
  return <div className="flex flex-wrap items-end gap-3 no-print">{children}</div>;
}

function Field({ label, wide, children }) {
  return (
    <div className={wide ? "min-w-[280px]" : "min-w-[140px]"}>
      <label className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

const selectCls =
  "w-full bg-white border border-[#d9cfb8] px-3 py-2 text-sm text-[#1c1a14] focus:outline-none focus:border-[#c8a04a]";

function Select({ testid, value, onChange, options }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={selectCls}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function PrimaryBtn({ children, testid, ...rest }) {
  return (
    <button
      data-testid={testid}
      {...rest}
      className="inline-flex items-center gap-2 bg-[#1c2a3a] hover:bg-[#0f1a2c] text-[#e7c879] px-4 py-2.5 text-xs uppercase tracking-widest disabled:opacity-60"
    >
      {children}
    </button>
  );
}

function SecondaryBtn({ children, testid, ...rest }) {
  return (
    <button
      data-testid={testid}
      {...rest}
      className="inline-flex items-center gap-2 bg-white border border-[#c8a04a] text-[#7a5b0f] hover:bg-[#fdf7e1] px-4 py-2.5 text-xs uppercase tracking-widest disabled:opacity-60"
    >
      {children}
    </button>
  );
}

function ErrorMsg({ error }) {
  if (!error) return null;
  return (
    <div
      data-testid={REPORTS.errorMsg}
      className="mt-3 px-4 py-2 bg-red-50 border border-red-200 text-red-700 text-xs"
    >
      {error}
    </div>
  );
}

function Stat({ label, value, highlight, isCount }) {
  return (
    <div className="border border-[#d9cfb8] bg-[#fbf7ee] px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">{label}</div>
      <div
        className={`font-serif text-xl tabular-nums ${
          highlight ? "text-[#7a5b0f]" : "text-[#1c1a14]"
        }`}
      >
        {isCount ? Number(value || 0) : `$ ${fmt(value)}`}
      </div>
    </div>
  );
}

function saveBlob(blob, filename, mime = "application/pdf") {
  const url = window.URL.createObjectURL(new Blob([blob], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function BoardPdfButton() {
  return null;
}

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/* -------------------- Import (Phase E) -------------------- */

function ImportTab() {
  return (
    <div data-testid={IMPORT.panel} className="grid gap-6 lg:grid-cols-2">
      <ImportCard
        title="Importar Miembros"
        description="Carga un .xlsx con miembros. Si el nombre ya existe se actualiza; si no, se crea con un ID nuevo."
        endpoint="/import/members.xlsx"
        templateEndpoint="/import/members.template.xlsx"
        templateName="plantilla_miembros.xlsx"
        ids={{
          file: IMPORT.membersFileInput,
          upload: IMPORT.membersUploadButton,
          template: IMPORT.membersTemplateButton,
          result: IMPORT.membersResult,
          error: IMPORT.membersError,
        }}
        columns={[
          "nombre (obligatorio)",
          "estado (Activo/Inactivo/Visitante)",
          "ministerio",
          "demografia (varón/mujer/joven/niño)",
          "ingreso (AAAA-MM-DD)",
          "email, telefono, cumpleanos, bautismo, notas",
        ]}
      />
      <ImportCard
        title="Importar Capturas Semanales"
        description="Carga un .xlsx con servicios dominicales o de jueves. Cada fila crea una captura nueva."
        endpoint="/import/captures.xlsx"
        templateEndpoint="/import/captures.template.xlsx"
        templateName="plantilla_capturas.xlsx"
        ids={{
          file: IMPORT.capturesFileInput,
          upload: IMPORT.capturesUploadButton,
          template: IMPORT.capturesTemplateButton,
          result: IMPORT.capturesResult,
          error: IMPORT.capturesError,
        }}
        columns={[
          "fecha (obligatorio, AAAA-MM-DD)",
          "tipo (Domingo/Jueves/Especial)",
          "diezmos, ofrenda, ofrenda_especial",
          "adultos, jovenes, ninos",
          "notas",
        ]}
      />
      <ImportCard
        title="Importar Gastos"
        description="Carga un .xlsx con gastos. Cada fila se inserta como un nuevo gasto."
        endpoint="/import/expenses.xlsx"
        templateEndpoint="/import/expenses.template.xlsx"
        templateName="plantilla_gastos.xlsx"
        ids={{
          file: IMPORT.expensesFileInput,
          upload: IMPORT.expensesUploadButton,
          template: IMPORT.expensesTemplateButton,
          result: IMPORT.expensesResult,
          error: IMPORT.expensesError,
        }}
        columns={[
          "fecha (obligatorio, AAAA-MM-DD)",
          "categoria (ver lista de categorías permitidas)",
          "descripcion",
          "metodo_pago (Efectivo/Transferencia/Cheque/Tarjeta/Otro)",
          "monto (obligatorio, > 0)",
          "notas",
        ]}
      />
    </div>
  );
}

function ImportCard({ title, description, endpoint, templateEndpoint, templateName, ids, columns }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const downloadTemplate = async () => {
    try {
      const res = await api.get(templateEndpoint, { responseType: "blob" });
      saveBlob(res.data, templateName, XLSX_MIME);
    } catch {
      setError("No se pudo descargar la plantilla");
    }
  };

  const upload = async () => {
    if (!file) {
      setError("Selecciona un archivo .xlsx");
      return;
    }
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(endpoint, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al importar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded border border-[#d9cfb8] bg-white p-5 shadow-sm">
      <header className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-widest text-[#1c1a14]">{title}</h3>
          <p className="mt-1 text-xs text-[#6b6450]">{description}</p>
        </div>
        <button
          data-testid={ids.template}
          onClick={downloadTemplate}
          className="inline-flex items-center gap-1 rounded border border-[#c8a04a] bg-[#fbf7ee] px-2 py-1 text-[10px] uppercase tracking-widest text-[#7a5b0f] hover:bg-[#f5edd3]"
        >
          <FileSpreadsheet size={12} /> Plantilla
        </button>
      </header>

      <div className="mb-3 rounded border border-dashed border-[#d9cfb8] bg-[#fbf7ee] p-3">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-[#7a5b0f]">Columnas esperadas</p>
        <ul className="ml-4 list-disc text-[11px] text-[#6b6450]">
          {columns.map((c) => <li key={c}>{c}</li>)}
        </ul>
      </div>

      <input
        data-testid={ids.file}
        type="file"
        accept=".xlsx"
        onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setError(""); }}
        className="block w-full text-xs text-[#1c1a14] file:mr-3 file:rounded file:border-0 file:bg-[#1c2a3a] file:px-3 file:py-1.5 file:text-[10px] file:uppercase file:tracking-widest file:text-[#e7c879] hover:file:bg-[#2a3b50]"
      />

      <div className="mt-3 flex items-center gap-2">
        <button
          data-testid={ids.upload}
          onClick={upload}
          disabled={busy || !file}
          className="inline-flex items-center gap-2 rounded bg-[#1c2a3a] px-4 py-2 text-[11px] uppercase tracking-widest text-[#e7c879] hover:bg-[#2a3b50] disabled:opacity-50"
        >
          <Upload size={14} /> {busy ? "Importando..." : "Importar"}
        </button>
        {file && <span className="text-[11px] text-[#6b6450]">{file.name}</span>}
      </div>

      {error && (
        <p data-testid={ids.error} className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </p>
      )}

      {result && (
        <div data-testid={ids.result} className="mt-3 rounded border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
          <div className="flex flex-wrap gap-x-4 gap-y-1 font-medium">
            {"inserted" in result && <span>Insertados: <b>{result.inserted}</b></span>}
            {"updated" in result && <span>Actualizados: <b>{result.updated}</b></span>}
            {"skipped" in result && <span>Omitidos: <b>{result.skipped}</b></span>}
          </div>
          {Array.isArray(result.errors) && result.errors.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-[11px] text-emerald-800">Ver {result.errors.length} aviso(s)</summary>
              <ul className="ml-4 mt-1 list-disc text-[11px] text-emerald-900">
                {result.errors.map((er, i) => <li key={`err-${i}-${String(er).slice(0, 32)}`}>{er}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
    </section>
  );
}

